/** Shared types for FHIR REST API layer. Used by the generic FhirApiService and all resource-specific facades. */

/** Minimal shape for any FHIR resource (required for put/create). */
export interface FhirResourceBase {
  resourceType: string;
  id?: string;
}

/** FHIR R4 Bundle.entry (search/transaction results). */
export interface FhirBundleEntry<T = unknown> {
  resource?: T;
  fullUrl?: string;
}

/** FHIR R4 Bundle (searchset, transaction, etc.). */
export interface FhirBundle<T = unknown> {
  resourceType: 'Bundle';
  type:
    | 'searchset'
    | 'transaction'
    | 'transaction-response'
    | 'batch'
    | 'batch-response'
    | 'history'
    | 'document'
    | 'message';
  total?: number;
  entry?: FhirBundleEntry<T>[];
}

/** Base search params supported by most resources (_id, _count, _offset, _summary, _elements, _lastUpdated). */
export interface FhirSearchParamsBase {
  _id?: string;
  _count?: number;
  _offset?: number;
  /** Filter by last updated date. Supports FHIR date prefixes (e.g. 'ge2026-01-01', 'le2026-12-31'). */
  _lastUpdated?: string | string[];
  /** Reduce payload: true | false | text | data */
  _summary?: boolean | 'true' | 'false' | 'text' | 'data';
  /** Comma-separated list of element names to return (sparse fieldset). */
  _elements?: string;
}

/**
 * Convert a params object to query record (exclude undefined/null). Supports string[] for repeated params like
 * _lastUpdated.
 */
export function toSearchParams(
  params?: Record<string, string | number | boolean | string[] | undefined> | object
): Record<string, string | string[]> | undefined {
  if (!params || typeof params !== 'object') return undefined;
  const out: Record<string, string | string[]> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      const filtered = v.filter((item): item is string => item !== undefined && item !== null);
      if (filtered.length) out[k] = filtered;
    } else {
      out[k] = String(v);
    }
  }
  return Object.keys(out).length ? out : undefined;
}

// ---------- Resource-specific search params (re-exported for convenience) ----------

export interface QuestionnaireSearchParams extends FhirSearchParamsBase {
  status?: string;
}

export interface QuestionnaireResponseSearchParams extends FhirSearchParamsBase {
  questionnaire?: string;
  status?: string;
  subject?: string;
  authored?: string;
}

export interface PatientSearchParams extends FhirSearchParamsBase {
  name?: string;
  family?: string;
  given?: string;
  birthdate?: string;
  gender?: string;
  identifier?: string;
  active?: boolean;
}

export interface PractitionerSearchParams extends FhirSearchParamsBase {
  name?: string;
  family?: string;
  given?: string;
  identifier?: string;
  active?: boolean;
}

export interface OrganizationSearchParams extends FhirSearchParamsBase {
  name?: string;
  identifier?: string;
  active?: boolean;
  partof?: string;
}

export interface RelatedPersonSearchParams extends FhirSearchParamsBase {
  patient?: string;
  name?: string;
  relationship?: string;
  identifier?: string;
  active?: boolean;
}

export interface EncounterSearchParams extends FhirSearchParamsBase {
  subject?: string;
  status?: string;
  type?: string;
  date?: string;
  patient?: string;
}

export interface AppointmentSearchParams extends FhirSearchParamsBase {
  patient?: string;
  status?: string;
  date?: string;
  practitioner?: string;
}

export interface LocationSearchParams extends FhirSearchParamsBase {
  name?: string;
  identifier?: string;
  status?: string;
  type?: string;
}
